import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaArrowRight,
  FaFilePdf,
  FaDownload
} from "react-icons/fa";
import { servicesData } from "./serviceData";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const OpenService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const serviceContentRef = useRef(null);
  
  const service = servicesData[id];

  if (!service) {
    return (
      <div style={{ 
        padding: "200px 20px", 
        textAlign: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        minHeight: "100vh"
      }}>
        <h1 style={{ fontSize: "48px", marginBottom: "20px" }}>404</h1>
        <h2 style={{ fontSize: "32px", marginBottom: "20px" }}>Service Not Found</h2>
        <p style={{ fontSize: "18px", marginBottom: "30px" }}>The service you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate("/#services")}
          style={{
            padding: "15px 40px",
            borderRadius: "30px",
            border: "none",
            background: "white",
            color: "#764ba2",
            fontSize: "18px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "transform 0.3s ease"
          }}
          onMouseEnter={(e) => e.target.style.transform = "scale(1.05)"}
          onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
        >
          Back to Services
        </button>
      </div>
    );
  }

  const handleOrderClick = (packageName) => {
    setSelectedPricing(packageName);
    const contact = document.getElementById("contactus");
    if (contact) {
      contact.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/#contactus";
    }
  };

  const generatePDF = async () => {
    if (!serviceContentRef.current) return;
    
    setIsGeneratingPDF(true);
    
    try {
      const element = serviceContentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        allowTaint: false,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width * 0.75, canvas.height * 0.75]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width * 0.75, canvas.height * 0.75);
      pdf.save(`${service.title.replace(/\s+/g, '_')}_Details.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div style={{
      paddingTop: "80px",
      minHeight: "100vh",
      position: "relative",
      overflow: "hidden",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
    }}>
      
      {/* Subtle Pattern Overlay */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 25px 25px, rgba(102, 126, 234, 0.05) 2%, transparent 0%), 
                         radial-gradient(circle at 75px 75px, rgba(118, 75, 162, 0.05) 2%, transparent 0%)`,
        backgroundSize: "100px 100px, 200px 200px",
        pointerEvents: "none",
        zIndex: 0
      }} />
      
      <div ref={serviceContentRef} style={{ position: "relative", zIndex: 1 }}>
        {/* Hero Section */}
        <div style={{
          background: "linear-gradient(135deg, #1a1f36 0%, #2a2145 50%, #1e293b 100%)",
          color: "white",
          padding: "100px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)"
        }}>
          {/* Professional subtle pattern */}
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.02) 25%, transparent 25%)",
            backgroundSize: "30px 30px"
          }} />
          
          <div style={{
            maxWidth: "900px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1
          }}>
            <div style={{
              marginBottom: "30px",
              filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.2))"
            }}>
              {service.icon}
            </div>
            <h1 style={{
              fontSize: "clamp(42px, 8vw, 64px)",
              marginBottom: "20px",
              fontWeight: "800",
              textShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }}>
              {service.title}
            </h1>
            <p style={{
              fontSize: "clamp(18px, 2.5vw, 24px)",
              opacity: 0.95,
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: "1.6",
              fontWeight: "300",
              textShadow: "0 1px 2px rgba(0,0,0,0.1)"
            }}>
              {service.shortDesc}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ 
          maxWidth: "1400px", 
          margin: "-50px auto 0", 
          padding: "0 20px 60px",
          position: "relative",
          zIndex: 2
        }}>
          {/* Detailed Description Card */}
          <div style={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderRadius: "30px",
            padding: "50px",
            marginBottom: "30px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
            border: "1px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 30px 60px -12px rgba(0,0,0,0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 25px 50px -12px rgba(0,0,0,0.15)";
          }}>
            <h2 style={{ 
              fontSize: "36px", 
              marginBottom: "25px", 
              color: "#1a1f36",
              position: "relative",
              paddingBottom: "15px",
              borderBottom: "4px solid",
              borderImage: "linear-gradient(90deg, #667eea, #764ba2) 1",
              borderBottomStyle: "solid"
            }}>
              About This Service
            </h2>
            <p style={{
              fontSize: "18px",
              lineHeight: "1.8",
              color: "#2d3748",
              marginBottom: "40px"
            }}>
              {service.longDesc}
            </p>

            {/* Key Features Grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
              marginTop: "30px"
            }}>
              {service.features.map((feature, index) => (
                <div key={index} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "15px",
                  background: "rgba(247,250,252,0.8)",
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  cursor: "default",
                  border: "1px solid rgba(102,126,234,0.1)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.background = "rgba(247,250,252,1)";
                  e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(102,126,234,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.background = "rgba(247,250,252,0.8)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                >
                  <FaCheckCircle color="#667eea" size={20} />
                  <span style={{ fontSize: "16px", color: "#1a202c" }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us Section */}
          <div style={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderRadius: "30px",
            padding: "50px",
            marginBottom: "30px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
            border: "1px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 30px 60px -12px rgba(0,0,0,0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 25px 50px -12px rgba(0,0,0,0.15)";
          }}>
            <h2 style={{ 
              fontSize: "36px", 
              marginBottom: "40px", 
              color: "#1a1f36",
              textAlign: "center",
              position: "relative",
              paddingBottom: "15px"
            }}>
              Why Choose Us
            </h2>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "30px"
            }}>
              {service.whyChooseUs.map((item, index) => (
                <div key={index} style={{
                  textAlign: "center",
                  padding: "30px 20px",
                  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  borderRadius: "20px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  transition: "all 0.3s ease",
                  border: "1px solid rgba(102,126,234,0.1)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-10px)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(102,126,234,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.05)";
                }}
                >
                  <div style={{
                    fontSize: "40px",
                    color: "#667eea",
                    marginBottom: "20px"
                  }}>
                    {item.icon}
                  </div>
                  <h3 style={{ fontSize: "22px", marginBottom: "10px", color: "#1a202c" }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: "16px", color: "#4a5568", lineHeight: "1.6" }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Process Section */}
          <div style={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderRadius: "30px",
            padding: "50px",
            marginBottom: "30px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
            border: "1px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 30px 60px -12px rgba(0,0,0,0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 25px 50px -12px rgba(0,0,0,0.15)";
          }}>
            <h2 style={{ 
              fontSize: "36px", 
              marginBottom: "40px", 
              color: "#1a1f36",
              textAlign: "center",
              position: "relative",
              paddingBottom: "15px"
            }}>
              Our Process
            </h2>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px"
            }}>
              {service.process.map((step, index) => (
                <div key={index} style={{
                  position: "relative",
                  padding: "30px 20px",
                  background: "#f8fafc",
                  borderRadius: "20px",
                  textAlign: "center",
                  borderLeft: index > 0 ? "2px dashed #667eea" : "none",
                  marginLeft: index > 0 ? "-10px" : "0",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.02)",
                  transition: "transform 0.3s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                >
                  <div style={{
                    width: "50px",
                    height: "50px",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    color: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    fontWeight: "bold",
                    margin: "0 auto 20px",
                    boxShadow: "0 10px 20px rgba(102,126,234,0.3)"
                  }}>
                    {step.step}
                  </div>
                  <h3 style={{ fontSize: "20px", marginBottom: "10px", color: "#1a202c" }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: "14px", color: "#4a5568", lineHeight: "1.6" }}>
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Section */}
          <div style={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderRadius: "30px",
            padding: "50px",
            marginBottom: "30px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
            border: "1px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 30px 60px -12px rgba(0,0,0,0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 25px 50px -12px rgba(0,0,0,0.15)";
          }}>
            <h2 style={{ 
              fontSize: "36px", 
              marginBottom: "10px", 
              color: "#1a1f36",
              textAlign: "center"
            }}>
              Pricing Plans
            </h2>
            <p style={{
              textAlign: "center",
              fontSize: "18px",
              color: "#4a5568",
              marginBottom: "40px"
            }}>
              Choose the perfect package for your needs
            </p>
            
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "30px",
              alignItems: "stretch"
            }}>
              {service.pricing.map((plan, index) => (
                <div key={index} style={{
                  position: "relative",
                  background: plan.popular ? "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)" : "white",
                  borderRadius: "20px",
                  padding: "40px 30px",
                  boxShadow: plan.popular ? "0 20px 40px rgba(102,126,234,0.2)" : "0 10px 30px rgba(0,0,0,0.05)",
                  transform: plan.popular ? "scale(1.05)" : "scale(1)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  border: plan.popular ? "2px solid #667eea" : "1px solid rgba(102,126,234,0.1)",
                  zIndex: plan.popular ? 2 : 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = plan.popular ? "scale(1.08)" : "scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 30px 60px rgba(102,126,234,0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = plan.popular ? "scale(1.05)" : "scale(1)";
                  e.currentTarget.style.boxShadow = plan.popular ? "0 20px 40px rgba(102,126,234,0.2)" : "0 10px 30px rgba(0,0,0,0.05)";
                }}
                >
                  {plan.badge && (
                    <div style={{
                      position: "absolute",
                      top: "-12px",
                      right: "20px",
                      background: "linear-gradient(135deg, #f43f5e, #ef4444)",
                      color: "white",
                      padding: "5px 20px",
                      borderRadius: "20px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      boxShadow: "0 5px 15px rgba(244,63,94,0.3)"
                    }}>
                      {plan.badge}
                    </div>
                  )}
                  
                  <div style={{
                    fontSize: "40px",
                    color: "#667eea",
                    marginBottom: "20px",
                    textAlign: "center"
                  }}>
                    {plan.icon}
                  </div>
                  
                  <h3 style={{
                    fontSize: "28px",
                    marginBottom: "10px",
                    color: "#1a202c",
                    textAlign: "center"
                  }}>
                    {plan.name}
                  </h3>
                  
                  <div style={{
                    textAlign: "center",
                    marginBottom: "30px"
                  }}>
                    <span style={{
                      fontSize: "36px",
                      fontWeight: "bold",
                      color: "#667eea"
                    }}>
                      {plan.price}
                    </span>
                    <span style={{
                      fontSize: "16px",
                      color: "#718096",
                      marginLeft: "5px"
                    }}>
                      /{plan.period}
                    </span>
                  </div>
                  
                  <ul style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "0 0 30px"
                  }}>
                    {plan.features.map((feature, idx) => (
                      <li key={idx} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "12px",
                        fontSize: "15px",
                        color: "#4a5568"
                      }}>
                        <FaCheckCircle color={plan.popular ? "#667eea" : "#10b981"} size={18} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => handleOrderClick(plan.name)}
                    style={{
                      width: "100%",
                      padding: "15px",
                      borderRadius: "10px",
                      border: "none",
                      background: plan.popular 
                        ? "linear-gradient(135deg, #667eea, #764ba2)"
                        : "linear-gradient(135deg, #f43f5e, #ef4444)",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      boxShadow: "0 5px 15px rgba(0,0,0,0.1)"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 5px 15px rgba(0,0,0,0.1)";
                    }}
                  >
                    {plan.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Deal Section - FIXED with Web Development style background */}
          {service.customDeal && service.customDeal.enabled && (
            <div style={{
              background: "linear-gradient(135deg, #1a1f36 0%, #2a2145 50%, #1e293b 100%)",
              borderRadius: "30px",
              padding: "60px 50px",
              marginBottom: "30px",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.3)",
              color: "white",
              textAlign: "center",
              position: "relative",
              overflow: "hidden"
            }}>
              {/* Professional subtle pattern - same as hero section */}
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.02) 25%, transparent 25%)",
                backgroundSize: "30px 30px"
              }} />
              
              <div style={{
                position: "absolute",
                top: "-50px",
                right: "-50px",
                width: "200px",
                height: "200px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "50%"
              }} />
              <div style={{
                position: "absolute",
                bottom: "-50px",
                left: "-50px",
                width: "200px",
                height: "200px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "50%"
              }} />
              
              <h2 style={{
                fontSize: "42px",
                marginBottom: "20px",
                position: "relative",
                zIndex: 1,
                textShadow: "0 2px 4px rgba(0,0,0,0.2)"
              }}>
                {service.customDeal.title}
              </h2>
              
              <p style={{
                fontSize: "18px",
                marginBottom: "30px",
                opacity: 0.95,
                maxWidth: "600px",
                marginLeft: "auto",
                marginRight: "auto",
                position: "relative",
                zIndex: 1
              }}>
                {service.customDeal.description}
              </p>
              
              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                flexWrap: "wrap",
                marginBottom: "30px",
                position: "relative",
                zIndex: 1
              }}>
                {service.customDeal.features.map((feature, idx) => (
                  <div key={idx} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(255,255,255,0.1)",
                    padding: "8px 20px",
                    borderRadius: "25px",
                    fontSize: "14px",
                    backdropFilter: "blur(5px)",
                    border: "1px solid rgba(255,255,255,0.1)"
                  }}>
                    <FaCheckCircle size={16} />
                    {feature}
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setShowCustomModal(true)}
                style={{
                  padding: "15px 40px",
                  borderRadius: "30px",
                  border: "none",
                  background: "white",
                  color: "#1a1f36",
                  fontSize: "18px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.05)";
                  e.target.style.boxShadow = "0 15px 35px rgba(0,0,0,0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 10px 25px rgba(0,0,0,0.2)";
                }}
              >
                Request Custom Quote <FaArrowRight style={{ marginLeft: "10px" }} />
              </button>
            </div>
          )}

          {/* FAQ Section */}
          <div style={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderRadius: "30px",
            padding: "50px",
            marginBottom: "30px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
            border: "1px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 30px 60px -12px rgba(0,0,0,0.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 25px 50px -12px rgba(0,0,0,0.15)";
          }}>
            <h2 style={{ 
              fontSize: "36px", 
              marginBottom: "40px", 
              color: "#1a1f36",
              textAlign: "center"
            }}>
              Frequently Asked Questions
            </h2>
            
            <div style={{
              maxWidth: "800px",
              margin: "0 auto"
            }}>
              {service.faq.map((item, index) => (
                <div key={index} style={{
                  marginBottom: "15px",
                  border: "1px solid rgba(102,126,234,0.2)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  background: "white"
                }}>
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    style={{
                      width: "100%",
                      padding: "20px",
                      textAlign: "left",
                      background: activeFaq === index ? "#f8fafc" : "white",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1a202c",
                      transition: "background 0.3s ease"
                    }}
                  >
                    {item.question}
                    <span style={{
                      transform: activeFaq === index ? "rotate(180deg)" : "rotate(0)",
                      transition: "transform 0.3s ease",
                      color: "#667eea"
                    }}>
                      ▼
                    </span>
                  </button>
                  {activeFaq === index && (
                    <div style={{
                      padding: "20px",
                      background: "#f8fafc",
                      borderTop: "1px solid rgba(102,126,234,0.1)",
                      color: "#4a5568",
                      lineHeight: "1.6"
                    }}>
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action - FIXED with Web Development style background */}
          <div style={{
            background: "linear-gradient(135deg, #1a1f36 0%, #2a2145 50%, #1e293b 100%)",
            borderRadius: "30px",
            padding: "60px 50px",
            textAlign: "center",
            color: "white",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.3)",
            position: "relative",
            overflow: "hidden"
          }}>
            {/* Professional subtle pattern - same as hero section */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.02) 25%, transparent 25%)",
              backgroundSize: "30px 30px"
            }} />
            
            <div style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "50%"
            }} />
            <div style={{
              position: "absolute",
              bottom: "-50px",
              left: "-50px",
              width: "200px",
              height: "200px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "50%"
            }} />
            
            <h2 style={{
              fontSize: "42px",
              marginBottom: "20px",
              position: "relative",
              zIndex: 1,
              textShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }}>
              Ready to Get Started?
            </h2>
            <p style={{
              fontSize: "18px",
              marginBottom: "30px",
              opacity: 0.95,
              maxWidth: "600px",
              marginLeft: "auto",
              marginRight: "auto",
              position: "relative",
              zIndex: 1
            }}>
              Contact us today to discuss your project and get a free consultation
            </p>
            <button
              onClick={() => {
                const contact = document.getElementById("contactus");
                if (contact) {
                  contact.scrollIntoView({ behavior: "smooth" });
                } else {
                  window.location.href = "/#contactus";
                }
              }}
              style={{
                padding: "15px 40px",
                borderRadius: "30px",
                border: "2px solid white",
                background: "transparent",
                color: "white",
                fontSize: "18px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                zIndex: 1
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "white";
                e.target.style.color = "#1a1f36";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "white";
              }}
            >
              Contact Us Now
            </button>
          </div>

          {/* PDF Download Button */}
          <div style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            zIndex: 9999
          }}>
            <button
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "15px 30px",
                borderRadius: "50px",
                border: "none",
                background: "linear-gradient(135deg, #1a1f36, #2a2145)",
                color: "white",
                fontSize: "16px",
                fontWeight: "600",
                cursor: isGeneratingPDF ? "wait" : "pointer",
                boxShadow: "0 10px 30px rgba(26,31,54,0.3)",
                transition: "all 0.3s ease",
                opacity: isGeneratingPDF ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!isGeneratingPDF) {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 15px 40px rgba(26,31,54,0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isGeneratingPDF) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(26,31,54,0.3)";
                }
              }}
            >
              {isGeneratingPDF ? (
                <>
                  <div style={{
                    width: "20px",
                    height: "20px",
                    border: "3px solid rgba(255,255,255,0.3)",
                    borderTopColor: "white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }} />
                  Generating PDF...
                </>
              ) : (
                <>
                  <FaFilePdf size={20} />
                  <FaDownload size={16} />
                  Save as PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Custom Deal Modal */}
      {showCustomModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(5px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: "20px"
        }}>
          <div style={{
            background: "white",
            borderRadius: "20px",
            padding: "40px",
            maxWidth: "500px",
            width: "100%",
            position: "relative",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
          }}>
            <button
              onClick={() => setShowCustomModal(false)}
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#666"
              }}
            >
              ×
            </button>
            
            <h2 style={{
              fontSize: "28px",
              marginBottom: "20px",
              color: "#1a1f36"
            }}>
              Request Custom Quote
            </h2>
            
            <p style={{
              color: "#4a5568",
              marginBottom: "30px",
              lineHeight: "1.6"
            }}>
              Tell us about your requirements and we'll create a personalized package for you.
            </p>
            
            <form onSubmit={(e) => e.preventDefault()}>
              <input
                type="text"
                placeholder="Your Name"
                style={{
                  width: "100%",
                  padding: "15px",
                  marginBottom: "15px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "16px",
                  transition: "border-color 0.3s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#667eea"}
                onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
              />
              <input
                type="email"
                placeholder="Your Email"
                style={{
                  width: "100%",
                  padding: "15px",
                  marginBottom: "15px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "16px",
                  transition: "border-color 0.3s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#667eea"}
                onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
              />
              <textarea
                placeholder="Tell us about your requirements..."
                rows="5"
                style={{
                  width: "100%",
                  padding: "15px",
                  marginBottom: "20px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  fontSize: "16px",
                  resize: "vertical",
                  transition: "border-color 0.3s ease"
                }}
                onFocus={(e) => e.target.style.borderColor = "#667eea"}
                onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
              />
              <button
                type="submit"
                onClick={() => {
                  setShowCustomModal(false);
                  alert("Thank you! We'll contact you soon with a custom quote.");
                }}
                style={{
                  width: "100%",
                  padding: "15px",
                  borderRadius: "10px",
                  border: "none",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  boxShadow: "0 5px 15px rgba(102,126,234,0.3)"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 10px 25px rgba(102,126,234,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 5px 15px rgba(102,126,234,0.3)";
                }}
              >
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          div {
            animation: slideIn 0.5s ease-out;
          }
        `}
      </style>
    </div>
  );
};

export default OpenService;